import React from "react";
import PropTypes from "prop-types";
import { Typography } from "@mui/material";
import dayjs from "dayjs";

import {
  UINumberField,
  UITextField,
  UITextarea,
  UIDatePicker,
  UIToggle,
} from "../../../../../ui";
import { getVisibleError } from "../helpers";
import MarginFields from "../MarginFields";
import { useSelector } from "react-redux";
import messages from "../../../../../../assets/locale/messages";
import TrainingLocationFields from "../TrainingLocationFields";
import { isPaidTrainingToggleOn } from "../../../../../../utils/paidTrainingRemoval";

const PaidTrainingSection = ({
  formik,
  roleIndex,
  shiftIndex,
  shift,
  onTrainingToggle,
  defaultTrainingBoundaryMeters,
  organizerShiftLocked,
  trainingScheduleLocked,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { events } = messages[lang] ?? messages.en ?? {};
  const t = events?.creatEvent?.defineRolesAndShifts;
  const ph = events?.creatEvent?.placeholders;
  
  const trainingBaseName = `event_roles_attributes.${roleIndex}.event_role_shifts_attributes.${shiftIndex}.training_attributes`;
  const trainingActive = isPaidTrainingToggleOn(shift.training_attributes);
  // Only a training whose saved start date has already passed needs past days
  // selectable (so editing it isn't blocked). A saved training scheduled in the
  // future — e.g. on an upcoming event — should still hide past days like a new one.
  const allowPastTrainingDates = Boolean(
    shift.training_attributes?.id &&
      trainingActive &&
      shift.training_attributes?.starts_at &&
      dayjs(shift.training_attributes.starts_at).isBefore(dayjs())
  );

  const freezeTrainingScheduleGeo =
    Boolean(organizerShiftLocked) || Boolean(trainingScheduleLocked);
  const freezePaidTrainingDetails =
    Boolean(organizerShiftLocked) || Boolean(trainingScheduleLocked);

  return (
    <div className="paid-training">
      <div className="paid-training__header">
        <div>
          <Typography className="paid-training__title text-heading-sm" component="span">
            {t.paidTraining}
          </Typography>
          <Typography className="paid-training__description text-body-xs" component="small">
            {t.paidTrainingDescription}
          </Typography>
        </div>
        <UIToggle
          id={`role-${roleIndex}-shift-${shiftIndex}-paid-training`}
          checked={trainingActive}
          onChange={onTrainingToggle}
          disabled={freezePaidTrainingDetails}
        />
      </div>

      {trainingActive && (
        <div className="paid-training__fields">
          <div className="roles-field-grid roles-field-grid--three">
            <UITextField
              label={t.trainingName}
              name={`${trainingBaseName}.name`}
              placeholder={ph?.trainingName}
              required
              disabled={freezePaidTrainingDetails}
              helperText={getVisibleError(
                formik,
                `${trainingBaseName}.name`,
              )}
            />
            <UINumberField
              label={t.trainingPaidAmountSar}
              name={`${trainingBaseName}.payroll_amount`}
              min={0}
              required
              placeholder={ph?.trainingPaidAmount}
              disabled={freezePaidTrainingDetails}
              helperText={getVisibleError(
                formik,
                `${trainingBaseName}.payroll_amount`,
              )}
            />
            <UINumberField
              label={t.trainingNumberOfDays}
              name={`${trainingBaseName}.number_of_days`}
              min={0}
              required
              placeholder={ph?.trainingNumberOfDays}
              disabled={freezePaidTrainingDetails}
              helperText={getVisibleError(
                formik,
                `${trainingBaseName}.number_of_days`,
              )}
            />
          </div>

          <div className="paid-training__description">
            <UITextarea
              label={t.trainingDescription}
              name={`${trainingBaseName}.description`}
              placeholder={ph?.trainingDescription}
              required
              minRows={4}
              disabled={freezePaidTrainingDetails}
              helperText={getVisibleError(
                formik,
                `${trainingBaseName}.description`,
              )}
            />
          </div>

          <div className="paid-training__timeline">
            <Typography
              className="paid-training__timeline-title text-label-sm"
              component="h3"
            >
              {t.trainingTimeline}
              <Typography
                className="paid-training__required-mark"
                component="span"
                aria-hidden
              >
                {" *"}
              </Typography>
            </Typography>
            <div className="roles-field-grid roles-field-grid--two">
              <UIDatePicker
                label={t.trainingStart}
                name={`${trainingBaseName}.starts_at`}
                required
                helperText={getVisibleError(
                  formik,
                  `${trainingBaseName}.starts_at`,
                )}
                withTime
                disablePast={!allowPastTrainingDates}
                disabled={freezeTrainingScheduleGeo}
              />
              <UIDatePicker
                label={t.trainingEnd}
                name={`${trainingBaseName}.ends_at`}
                required
                helperText={getVisibleError(
                  formik,
                  `${trainingBaseName}.ends_at`,
                )}
                withTime
                disablePast={!allowPastTrainingDates}
                disabled={freezeTrainingScheduleGeo}
              />
            </div>
          </div>
          <MarginFields
            formik={formik}
            baseName={trainingBaseName}
            mode="training"
            disabled={freezeTrainingScheduleGeo}
          />
          <TrainingLocationFields
            formik={formik}
            baseName={trainingBaseName}
            mode="training"
            defaultBoundaryMetersFromEvent={defaultTrainingBoundaryMeters}
            interactionsDisabled={freezeTrainingScheduleGeo}
          />
        </div>
      )}
    </div>
  );
};

PaidTrainingSection.propTypes = {
  formik: PropTypes.object.isRequired,
  roleIndex: PropTypes.number.isRequired,
  shiftIndex: PropTypes.number.isRequired,
  shift: PropTypes.object.isRequired,
  onTrainingToggle: PropTypes.func.isRequired,
  defaultTrainingBoundaryMeters: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  organizerShiftLocked: PropTypes.bool,
  trainingScheduleLocked: PropTypes.bool,
};

PaidTrainingSection.defaultProps = {
  organizerShiftLocked: false,
  trainingScheduleLocked: false,
};

export default PaidTrainingSection;
